import { Routes, Route } from 'react-router-dom';

import AuthLayout from '../components/layout/AuthLayout';
import DashboardLayout from '../components/layout/DashboardLayout';
import ProtectedRoute from './ProtectedRoute';
import RoleRoute from './RoleRoute';

import Home from '../pages/Home';
import Login from '../pages/auth/Login';
import Register from '../pages/auth/Register';
import VerifyEmail from '../pages/auth/VerifyEmail';
import TwoFactor from '../pages/auth/TwoFactor';
import ForgotPassword from '../pages/auth/ForgotPassword';
import ResetPassword from '../pages/auth/ResetPassword';
import MagicLinkSent from '../pages/auth/MagicLinkSent';
import MagicLinkConsume from '../pages/auth/MagicLinkConsume';
import ChangePassword from '../pages/auth/ChangePassword';

import InvestorDashboard from '../pages/investor/Dashboard';
import KycForm from '../pages/investor/KycForm';
import Plans from '../pages/investor/Plans';
import PlanDetail from '../pages/investor/PlanDetail';
import Investments from '../pages/investor/Investments';
import InvestorInvestmentDetail from '../pages/investor/InvestmentDetail';
import Statements from '../pages/investor/Statements';
import Tickets from '../pages/investor/Tickets';
import NewTicket from '../pages/investor/NewTicket';
import TicketDetail from '../pages/investor/TicketDetail';
import InvestorReports from '../pages/investor/Reports';

import AdminOverview from '../pages/admin/Overview';
import InvestorsList from '../pages/admin/InvestorsList';
import InvestorDetail from '../pages/admin/InvestorDetail';
import InvestmentsList from '../pages/admin/InvestmentsList';
import InvestmentDetail from '../pages/admin/InvestmentDetail';
import InvestmentForm from '../pages/admin/InvestmentForm';
import AdminPlans from '../pages/admin/Plans';
import KycReview from '../pages/admin/KycReview';
import KycReviewDetail from '../pages/admin/KycReviewDetail';
import KycTemplates from '../pages/admin/KycTemplates';
import AdminTickets from '../pages/admin/Tickets';
import AdminTicketDetail from '../pages/admin/TicketDetail';
import Reports from '../pages/admin/Reports';
import AuditLog from '../pages/admin/AuditLog';
import AdminAccounts from '../pages/admin/AdminAccounts';
import UnlockAccount from '../pages/admin/UnlockAccount';
import OnboardClient from '../pages/admin/OnboardClient';
import NotFound from '../pages/NotFound';

export default function AppRoutes() {
  return (
    <Routes>
      {/* Public marketing homepage — visitors see this before logging in. */}
      <Route path="/" element={<Home />} />

      <Route element={<AuthLayout />}>
        <Route path="/login" element={<Login />} />
        <Route path="/login/2fa" element={<TwoFactor />} />
        <Route path="/register" element={<Register />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/magic-link/sent" element={<MagicLinkSent />} />
        {/* /auth/magic-link is the real path the backend's mail template
            links to (confirmed from an actual emailed link) — /magic-link/
            consume was this app's original guess and is kept as an alias
            in case anything else still points at it. */}
        <Route path="/auth/magic-link" element={<MagicLinkConsume />} />
        <Route path="/magic-link/consume" element={<MagicLinkConsume />} />
      </Route>

      {/* Forced password-change gate — reachable while authenticated even
          though it lives outside DashboardLayout, since must_change_password
          blocks every other protected endpoint. */}
      <Route element={<ProtectedRoute />}>
        <Route element={<AuthLayout />}>
          <Route path="/change-password" element={<ChangePassword />} />
        </Route>
      </Route>

      <Route element={<ProtectedRoute />}>
        <Route element={<DashboardLayout />}>
          {/* Investor */}
          <Route element={<RoleRoute allow={['investor']} />}>
            <Route path="/dashboard" element={<InvestorDashboard />} />
            <Route path="/kyc" element={<KycForm />} />
            <Route path="/plans" element={<Plans />} />
            <Route path="/plans/:id" element={<PlanDetail />} />
            <Route path="/my-investments" element={<Investments />} />
            <Route path="/my-investments/:id" element={<InvestorInvestmentDetail />} />
            <Route path="/statements" element={<Statements />} />
            <Route path="/tickets" element={<Tickets />} />
            <Route path="/tickets/new" element={<NewTicket />} />
            <Route path="/tickets/:id" element={<TicketDetail />} />
            <Route path="/reports" element={<InvestorReports />} />
          </Route>

          {/* Admin & Super Admin */}
          <Route element={<RoleRoute allow={['admin', 'super-admin']} />}>
            <Route path="/admin" element={<AdminOverview />} />
            <Route path="/admin/investors" element={<InvestorsList />} />
            <Route path="/admin/investors/:id" element={<InvestorDetail />} />
            <Route path="/admin/investments" element={<InvestmentsList />} />
            <Route path="/admin/investments/new" element={<InvestmentForm />} />
            <Route path="/admin/investments/:id" element={<InvestmentDetail />} />
            <Route path="/admin/investments/:id/edit" element={<InvestmentForm />} />
            <Route path="/admin/plans" element={<AdminPlans />} />
            <Route path="/admin/kyc" element={<KycReview />} />
            <Route path="/admin/kyc/:id" element={<KycReviewDetail />} />
            {/* v1.18.0 — Template Management, kept as its own top-level path
                (not nested under /admin/kyc/*) to avoid any ambiguity with
                the /admin/kyc/:id submission-detail route above. */}
            <Route path="/admin/kyc-templates" element={<KycTemplates />} />
            <Route path="/admin/tickets" element={<AdminTickets />} />
            <Route path="/admin/tickets/:id" element={<AdminTicketDetail />} />
            <Route path="/admin/reports" element={<Reports />} />
            {/* Kept inside the general admin+super-admin RoleRoute block
                (not the super-admin-only block below) so a plain admin who
                lands here directly reaches the page and sees its own
                explicit "no permission" message rather than being silently
                redirected to "/" by RoleRoute — see AuditLog.jsx. The real
                access control is the API's super-admin-only check either
                way; the sidebar link is also only ever rendered for
                super-admins (Sidebar.jsx). */}
            <Route path="/admin/audit-logs" element={<AuditLog />} />
            <Route path="/admin/unlock-account" element={<UnlockAccount />} />
            <Route path="/admin/onboard-client" element={<OnboardClient />} />
          </Route>

          {/* Super Admin only */}
          <Route element={<RoleRoute allow={['super-admin']} />}>
            <Route path="/admin/admins" element={<AdminAccounts />} />
          </Route>
        </Route>
      </Route>

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
